// CISA SCuBA (Secure Cloud Business Applications) Compliance Types

export interface ScubaReport {
  id: string
  tenantId: string
  scanDate: string
  scannedBy: string
  scubaVersion: string
  totalPasses: number
  totalFailures: number
  totalWarnings: number
  totalErrors: number
  totalManual: number
  totalControls: number
  complianceScore: number
  products: ScubaProductResult[]
  scanJobId?: string
}

export interface ScubaProductResult {
  productName: string
  productDisplayName: string
  passes: number
  failures: number
  warnings: number
  errors: number
  manual: number
  totalControls: number
  complianceScore: number
  controlGroups: ScubaControlGroup[]
}

export interface ScubaControlGroup {
  groupName: string
  groupNumber: string
  groupReferenceUrl?: string
  controls: ScubaControl[]
  passes: number
  failures: number
  warnings: number
  errors: number
  manual: number
}

export interface ScubaControl {
  controlId: string
  requirement: string
  result: ScubaControlResult
  criticality: string
  details: string
}

export type ScubaControlResult = 'Pass' | 'Fail' | 'Warning' | 'Error' | 'NotApplicable'

export interface ScubaSummary {
  tenantId: string
  hasReport: boolean
  lastScanDate?: string
  scannedBy?: string
  complianceScore: number
  totalPasses: number
  totalFailures: number
  totalWarnings: number
  totalErrors: number
  totalManual: number
  totalControls: number
  products: ScubaProductSummary[]
}

export interface ScubaProductSummary {
  productName: string
  productDisplayName: string
  complianceScore: number
  passes: number
  failures: number
  warnings: number
  totalControls: number
}

export interface ScubaSecurityRisk {
  productName: string
  productDisplayName: string
  groupName: string
  requirement: string
  details: string
  criticality: string
  result: ScubaControlResult
}

export interface ScubaScanResponse {
  jobId: string
  status: string
  message: string
}

// Product styling information for UI
export interface ScubaProductInfo {
  productName: string
  displayName: string
  iconName: string
  color: string
  bgColor: string
}

// Product styling map
export const SCUBA_PRODUCTS: Record<string, ScubaProductInfo> = {
  aad: {
    productName: 'aad',
    displayName: 'Entra ID',
    iconName: 'ShieldCheckIcon',
    color: '#3b82f6',
    bgColor: 'bg-blue-100'
  },
  sharepoint: {
    productName: 'sharepoint',
    displayName: 'SharePoint',
    iconName: 'FolderIcon',
    color: '#14b8a6',
    bgColor: 'bg-teal-100'
  },
  exo: {
    productName: 'exo',
    displayName: 'Exchange Online',
    iconName: 'EnvelopeIcon',
    color: '#8b5cf6',
    bgColor: 'bg-purple-100'
  },
  teams: {
    productName: 'teams',
    displayName: 'Teams',
    iconName: 'ChatBubbleLeftRightIcon',
    color: '#6366f1',
    bgColor: 'bg-indigo-100'
  },
  defender: {
    productName: 'defender',
    displayName: 'Defender',
    iconName: 'ShieldExclamationIcon',
    color: '#ef4444',
    bgColor: 'bg-red-100'
  }
}

// Helper function to get product info
export function getScubaProductInfo(productName: string): ScubaProductInfo {
  const normalizedName = productName.toLowerCase()
  return SCUBA_PRODUCTS[normalizedName] ?? {
    productName: normalizedName,
    displayName: productName,
    iconName: 'QuestionMarkCircleIcon',
    color: '#6b7280',
    bgColor: 'bg-gray-100'
  }
}

// Result badge color mapping
export function getResultColor(result: ScubaControlResult): string {
  switch (result) {
    case 'Pass':
      return 'bg-green-100 text-green-800'
    case 'Fail':
      return 'bg-red-100 text-red-800'
    case 'Warning':
      return 'bg-yellow-100 text-yellow-800'
    case 'Error':
      return 'bg-orange-100 text-orange-800'
    case 'NotApplicable':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

// Result badge text
export function getResultText(result: ScubaControlResult): string {
  switch (result) {
    case 'Pass':
      return 'PASS'
    case 'Fail':
      return 'FAIL'
    case 'Warning':
      return 'WARN'
    case 'Error':
      return 'ERROR'
    case 'NotApplicable':
      return 'N/A'
    default:
      return result
  }
}
